/**
 * Empire-PWA chassis · DPDP-2023 consent registry.
 *
 * DPDP requires every Data Principal (the user) to give "free, specific,
 * informed, unconditional, and unambiguous" consent before any processing.
 * This module is the local, append-only ledger of those consents. Every
 * grant / withdraw is logged with a timestamp + scope + purpose, and the
 * ledger is exportable on request (right-to-access).
 *
 * v0: local ledger only. v1.1 wires the ledger into a Coolify-hosted
 * Consent Manager record (per DPDP §6) when the user agrees to cloud sync.
 */
import type { Iso8601 } from './branded.ts';
import { readFile, writeFile, appendWriteLog } from './storage.ts';

export type ConsentScope =
  | 'camera'
  | 'microphone'
  | 'photos_local_only'
  | 'wardrobe_data'
  | 'biometric_passkey'
  | 'cloud_sync_r2'
  | 'marketing_email'
  | 'crash_telemetry';

export type ConsentPurpose =
  | 'capture_wardrobe_photos'
  | 'voice_entry'
  | 'unlock_app'
  | 'multi_device_sync'
  | 'product_updates'
  | 'usage_analytics';

export interface ConsentRecord {
  readonly id: string;
  readonly ts: Iso8601;
  readonly scope: ConsentScope;
  readonly purpose: ConsentPurpose;
  readonly granted: boolean;
  readonly note: string | null;
}

const FILE = 'almari-consent-ledger.json';

export async function record(scope: ConsentScope, purpose: ConsentPurpose, granted: boolean, note: string | null = null): Promise<ConsentRecord> {
  const log = (await readFile<ConsentRecord[]>(FILE)) ?? [];
  const rec: ConsentRecord = {
    id: crypto.randomUUID(),
    ts: new Date().toISOString() as Iso8601,
    scope,
    purpose,
    granted,
    note,
  };
  log.push(rec);
  await writeFile(FILE, log);
  await appendWriteLog({ path: FILE, op: 'append', bytes: JSON.stringify(rec).length });
  return rec;
}

export async function ledger(): Promise<readonly ConsentRecord[]> {
  return (await readFile<ConsentRecord[]>(FILE)) ?? [];
}

export async function isGranted(scope: ConsentScope, purpose: ConsentPurpose): Promise<boolean> {
  const all = await ledger();
  let last: ConsentRecord | null = null;
  for (const r of all) {
    if (r.scope === scope && r.purpose === purpose) {
      if (!last || r.ts > last.ts) last = r;
    }
  }
  return last !== null && last.granted;
}

export async function withdrawAll(): Promise<void> {
  const all = await ledger();
  const ts = new Date().toISOString() as Iso8601;
  const out: ConsentRecord[] = [...all];
  const seen = new Set<string>();
  for (const r of all) {
    const k = `${r.scope}|${r.purpose}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ id: crypto.randomUUID(), ts, scope: r.scope, purpose: r.purpose, granted: false, note: 'bulk withdraw' });
  }
  await writeFile(FILE, out);
}

export async function exportLedger(): Promise<Blob> {
  const all = await ledger();
  return new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
}
